import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiLogOut, FiMenu, FiX } from "react-icons/fi";
import { useAuthStore, useLanguageStore } from "../store";
import "../styles/landing.css";

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuthStore(function (state) {
    return { user: state.user, logout: state.logout };
  });
  const { language, setLanguage } = useLanguageStore();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const botonDeMenuRef = useRef(null);
  const ultimoControlDelMenuRef = useRef(null);

  function rutaActiva(ruta) {
    if (ruta === "/interviews") {
      return location.pathname.startsWith("/interview");
    }
    return location.pathname === ruta;
  }

  useEffect(function () {
    setMenuAbierto(false);
  }, [location.pathname]);

  useEffect(function () {
    if (!menuAbierto) {
      return undefined;
    }
    document.body.style.overflow = "hidden";
    /* El overlay no es modal para ARIA (el botón de cierre vive en la barra),
       así que el contenido tapado se aísla de los lectores con inert */
    const contenidoPrincipal = document.getElementsByTagName("main")[0];
    contenidoPrincipal?.setAttribute("inert", "");

    function manejarTeclaEscape(evento) {
      if (evento.key === "Escape") {
        setMenuAbierto(false);
        botonDeMenuRef.current?.focus();
      }
    }
    document.addEventListener("keydown", manejarTeclaEscape);
    return function () {
      document.body.style.overflow = "";
      contenidoPrincipal?.removeAttribute("inert");
      document.removeEventListener("keydown", manejarTeclaEscape);
    };
  }, [menuAbierto]);

  const enlaces = user
    ? [
        { texto: t("panel.navPanel"), ruta: "/dashboard" },
        { texto: t("panel.navEntrevistas"), ruta: "/interviews" },
        { texto: t("panel.navSuscripcion"), ruta: "/subscription" },
        { texto: t("panel.navAjustes"), ruta: "/settings" },
      ]
    : [];

  function cerrarSesion() {
    logout();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  }

  function cambiarIdioma(evento) {
    const idioma = evento.target.value;
    setLanguage(idioma);
    i18n.changeLanguage(idioma);
  }

  function cerrarMenu() {
    setMenuAbierto(false);
    botonDeMenuRef.current?.focus();
  }

  function alternarMenu() {
    setMenuAbierto(function (abierto) {
      return !abierto;
    });
  }

  /* El foco vuelve al botón de menú al salir del último control: el overlay
     tapa la página y dejar escapar el tabulador la haría inaccesible */
  function atraparTabulador(evento) {
    if (evento.key === "Tab" && !evento.shiftKey) {
      evento.preventDefault();
      botonDeMenuRef.current?.focus();
    }
  }

  /* El sentido inverso del mismo cerrojo: Shift+Tab desde el primer enlace
     salta al último control en vez de escapar del overlay */
  function atraparTabuladorInverso(evento) {
    if (evento.key === "Tab" && evento.shiftKey) {
      evento.preventDefault();
      ultimoControlDelMenuRef.current?.focus();
    }
  }

  /* Sin enlaces (usuario aún sin hidratar) el selector de idioma es el
     primer Y el último control: necesita el cerrojo en los dos sentidos */
  function atraparTabuladorEnAmbosSentidos(evento) {
    atraparTabulador(evento);
    atraparTabuladorInverso(evento);
  }

  if (["/", "/login", "/register"].includes(location.pathname)) {
    return null;
  }

  return (
    <>
    <header className="panel sticky top-0 z-50 border-b border-linea bg-[#0b0b0d]/90 backdrop-blur-xl">
      <nav
        aria-label="Principal"
        className="mx-auto flex h-14 w-full max-w-[1400px] items-center justify-between gap-6 px-6 sm:px-10 lg:px-16"
      >
        <span className="flex min-w-0 items-center gap-10">
          <Link
            to="/"
            aria-label="PreguntaT inicio"
            className="flex shrink-0 items-center gap-2.5"
          >
            <span aria-hidden="true" className="marca__ecualizador">
              <span className="marca__ecualizador__barra marca__ecualizador__barra--a1"></span>
              <span className="marca__ecualizador__barra marca__ecualizador__barra--a2"></span>
              <span className="marca__ecualizador__barra marca__ecualizador__barra--a3"></span>
            </span>
            <span className="tipo-display text-lg text-tinta">
              Pregunta<span className="text-esmeralda">T</span>
            </span>
          </Link>

          <ul className="hidden h-14 items-center gap-1 min-[901px]:flex">
            {enlaces.map(function (enlace) {
              const activa = rutaActiva(enlace.ruta);
              return (
                <li key={enlace.ruta} className="flex h-full">
                  <Link
                    to={enlace.ruta}
                    aria-current={activa ? "page" : undefined}
                    className={`relative flex h-full items-center px-3 text-sm font-medium transition-colors ${
                      activa ? "text-tinta" : "text-tintaSuave hover:text-tinta"
                    }`}
                  >
                    {enlace.texto}
                    {activa && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-esmeralda"
                      />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </span>

        <span className="flex shrink-0 items-center gap-3">
          <select
            value={language}
            onChange={cambiarIdioma}
            aria-label={t("panel.idioma")}
            className="campo-selector hidden rounded-lg border border-transparent bg-transparent py-1.5 pl-2.5 pr-7 font-mono text-[11px] uppercase tracking-[0.12em] text-tintaSuave transition-colors hover:border-linea focus-visible:outline focus-visible:outline-2 focus-visible:outline-esmeralda min-[901px]:block"
          >
            <option value="es">ES</option>
            <option value="en">EN</option>
            <option value="fr">FR</option>
            <option value="de">DE</option>
          </select>

          {user && (
            <button
              onClick={cerrarSesion}
              aria-label={t("panel.cerrarSesion")}
              className="hidden h-9 w-9 items-center justify-center rounded-lg border border-transparent text-tintaSuave transition-colors hover:border-linea hover:text-tinta focus-visible:outline focus-visible:outline-2 focus-visible:outline-esmeralda min-[901px]:flex"
            >
              <FiLogOut size={15} aria-hidden="true" />
            </button>
          )}

          <button
            ref={botonDeMenuRef}
            onClick={alternarMenu}
            aria-label={t("panel.menu")}
            aria-expanded={menuAbierto}
            aria-controls="menu-movil"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-linea text-tinta focus-visible:outline focus-visible:outline-2 focus-visible:outline-esmeralda min-[901px]:hidden"
          >
            {menuAbierto ? (
              <FiX size={18} aria-hidden="true" />
            ) : (
              <FiMenu size={18} aria-hidden="true" />
            )}
          </button>
        </span>
      </nav>
    </header>

    <section
      id="menu-movil"
      role="dialog"
      aria-label="Menú móvil"
      hidden={!menuAbierto}
      className={`${
        menuAbierto ? "flex" : "hidden"
      } panel fixed inset-0 z-40 flex-col justify-between overflow-y-auto bg-[#0b0b0d]/96 px-6 pb-10 pt-24 backdrop-blur-2xl min-[901px]:hidden`}
    >
      <ul className="flex flex-col">
        {enlaces.map(function (enlace, indice) {
          return (
            <li
              key={enlace.ruta}
              className="menu-movil__elemento border-b border-white/[0.06]"
            >
              <Link
                to={enlace.ruta}
                onClick={cerrarMenu}
                onKeyDown={indice === 0 ? atraparTabuladorInverso : undefined}
                aria-current={rutaActiva(enlace.ruta) ? "page" : undefined}
                className="flex items-baseline gap-4 py-6"
              >
                <span className="font-mono text-[11px] tracking-[0.2em] text-esmeralda">
                  ( {String(indice + 1).padStart(2, "0")} )
                </span>
                <span
                  className={`tipo-display text-4xl ${
                    rutaActiva(enlace.ruta) ? "text-esmeralda" : "text-tinta"
                  }`}
                >
                  {enlace.texto}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      <span className="menu-movil__elemento flex items-center justify-between gap-3">
        <select
          ref={user ? undefined : ultimoControlDelMenuRef}
          value={language}
          onChange={cambiarIdioma}
          aria-label={t("panel.idioma")}
          className="campo-selector rounded-full border border-white/10 bg-transparent py-2.5 pl-4 pr-8 font-mono text-[11px] uppercase tracking-[0.14em] text-tintaSuave focus-visible:outline focus-visible:outline-2 focus-visible:outline-esmeralda"
          onKeyDown={user ? undefined : atraparTabuladorEnAmbosSentidos}
        >
          <option value="es">ES</option>
          <option value="en">EN</option>
          <option value="fr">FR</option>
          <option value="de">DE</option>
        </select>
        {user && (
          <button
            ref={ultimoControlDelMenuRef}
            onClick={function () {
              cerrarSesion();
              cerrarMenu();
            }}
            onKeyDown={atraparTabulador}
            className="flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-tintaSuave"
          >
            <FiLogOut size={14} aria-hidden="true" /> {t("panel.cerrarSesion")}
          </button>
        )}
      </span>
    </section>
    </>
  );
}

export default Header;
